import { Line, LineConfig } from '@ant-design/charts';
import { useLazyQuery } from '@apollo/client';
import {
  GET_IDEA_COUNT_BY_STATUS,
  IdeaCountByDays,
  IdeaCountByDaysChartData,
} from '@frontend/data-access';
import { Card } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

const COLOR_PLATE_10 = [
  '#529FF0',
  '#63E063',
  '#FFB547',
  '#0273EA',
  '#B1BDC9',
  '#7AC4FC',
  '#C5F1BB',
  '#A3B1BF',
  '#8B8B8B',
  '#525355',
];

interface IdeaCountByStatusProps {
  className?: string;
  channelId?: string;
  challengeId?: string;
  innovationSpaceId?: string;
}

function IdeaCountByStatus({
  className,
  channelId,
  challengeId,
  innovationSpaceId,
}: IdeaCountByStatusProps) {
  const { t } = useTranslation('common');
  const [data, setData] = useState<IdeaCountByDaysChartData[]>([]);

  const [getIdeaCountByStatus, { loading }] = useLazyQuery<{
    ideaPhaseCountByDates: IdeaCountByDays[];
  }>(GET_IDEA_COUNT_BY_STATUS, {
    onCompleted(data) {
      if (data.ideaPhaseCountByDates == null) {
        setData([]);
        return;
      }
      const newData: IdeaCountByDaysChartData[] = [];
      // Add everything into a list
      for (let i = data.ideaPhaseCountByDates.length - 1; i >= 0; i--) {
        const item = data.ideaPhaseCountByDates[i];
        for (const stageCount of item.stageCount) {
          newData.push({
            days: item.days,
            items: stageCount.items,
            phase: stageCount.phase,
          });
        }
      }
      setData(newData);
    },
    onError(error) {
      setData([]);
    },
  });

  const monthNames = [
    t('Jan'),
    t('Feb'),
    t('Mar'),
    t('Apr'),
    t('May'),
    t('Jun'),
    t('Jul'),
    t('Aug'),
    t('Sep'),
    t('Oct'),
    t('Nov'),
    t('Dec'),
  ];

  useEffect(() => {
    if (!channelId) return;
    getIdeaCountByStatus({
      variables: {
        startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
        channelId,
        challengeId,
        spaceId: innovationSpaceId,
      },
    });
  }, [channelId, challengeId, innovationSpaceId, getIdeaCountByStatus]);

  const chartData = useMemo(() => {
    if (data.length > 0) {
      return data;
    }
    // Return placeholder data with 0 count when no data is available
    const today = dayjs().format('YYYY-MM-DD');
    return [{ days: today, items: 0, phase: t('no_data') }];
  }, [data, t]);

  const config: LineConfig = {
    data: chartData,
    loading,
    xField: 'days',
    yField: 'items',
    colorField: 'phase',
    seriesField: 'phase',
    animate: { enter: { type: 'pathIn' } },
    style: { lineWidth: 2 },
    // point: { shape: 'circle' },
    scale: {
      color: {
        range: COLOR_PLATE_10,
      },
    },
    smooth: false,
    xAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
        },
        // Ensure y-axis starts from 0 and includes at least value 1
        min: 0,
        max: chartData.some((item) => item.items > 0) ? undefined : 1,
      },
      x: {
        label: {
          style: {
            fontFamily: 'Lufga',
          },
        },
        labelFormatter: (v: string) => {
          const monthIndex = parseInt(v.split('-')[1], 10) - 1;
          return monthNames[monthIndex];
        },
      },
    },
    paddingTop: 32,
    legend: {
      color: {
        position: 'bottom',
        autoWrap: true,
        itemMarker: 'square',
        style: {
          fontFamily: 'Lufga',
        },
        layout: {
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
    interactions: [
      {
        type: 'legend-filter',
        enable: true,
      },
    ],
    animation: {
      appear: {
        animation: 'path-in',
        duration: 100,
      },
    },
  };

  return (
    <Card
      style={{ borderRadius: 20 }}
      className={`${className}`}
      styles={{ body: { height: '100%' } }}
    >
      <div className="h-full flex flex-col space-y-4 justify-between">
        <div className="text-sm text-gray-900 font-medium">
          {t('ideas_status')}
        </div>
        <div className="h-full flex-grow">
          <Line {...config} />
        </div>
      </div>
    </Card>
  );
}

export default IdeaCountByStatus;
